import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth';
import { flushDeliveries, notSentDeliveries, onDeliveryQueueChange, queuedDeliveries } from '../deliveryQueue';
import { qk } from '../queryClient';

/** What waits on this phone for the signed-in user and the not-sent list, kept current, with Send now (decision 109). */
export function useDeliveryQueue() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const userId = me?.id;
  const [queued, setQueued] = useState(() => queuedDeliveries(userId));
  const [notSent, setNotSent] = useState(() => notSentDeliveries(userId));
  const [sending, setSending] = useState(false);
  useEffect(() => {
    const refresh = () => {
      setQueued(queuedDeliveries(userId));
      setNotSent(notSentDeliveries(userId));
    };
    refresh();
    return onDeliveryQueueChange(refresh);
  }, [userId]);
  const flush = useCallback(async () => {
    if (!userId || queuedDeliveries(userId).length === 0) return;
    setSending(true);
    try {
      const r = await flushDeliveries((id, body) => api.deliverStop(id, body), userId);
      if (r.sent || r.refused.length) {
        void qc.invalidateQueries({ queryKey: ['routes'] });
        void qc.invalidateQueries({ queryKey: ['orders'] });
      }
      // signed out on the road: sign in again and they go; nothing is dropped
      if (r.signedOut) qc.setQueryData(qk.me, null);
    } finally {
      setSending(false);
    }
  }, [qc, userId]);
  return { queued, notSent, sending, flush };
}

/**
 * Sends the deliveries kept on the phone from any screen: when the app opens, when the signal comes back, and every
 * minute while it is open (decision 109). Mounted once in the layout.
 */
export function DeliverySync() {
  const { flush } = useDeliveryQueue();
  useEffect(() => {
    void flush();
    const online = () => void flush();
    window.addEventListener('online', online);
    const timer = window.setInterval(() => void flush(), 60_000);
    return () => {
      window.removeEventListener('online', online);
      window.clearInterval(timer);
    };
  }, [flush]);
  return null;
}
